// src/constants/goalTypes.js

/**
 * 12-Week Year Goal System
 * Based on Brian Moran's methodology:
 * - 12-week execution cycles create urgency
 * - Weekly planning and scoring
 * - Lead measures (actions) drive lag measures (results)
 * - Model weeks for time blocking
 */

// Goal Status
export const GOAL_STATUS = {
  DRAFT: { id: 'draft', label: 'Draft', color: 'gray' },
  ACTIVE: { id: 'active', label: 'Active', color: 'blue' },
  COMPLETED: { id: 'completed', label: 'Completed', color: 'green' },
  ABANDONED: { id: 'abandoned', label: 'Abandoned', color: 'red' },
  PAUSED: { id: 'paused', label: 'Paused', color: 'amber' }
};

// Goal Categories (Life Domains)
export const GOAL_CATEGORIES = {
  CAREER: { id: 'career', label: 'Career / Work', icon: 'Briefcase', color: 'blue' },
  BUSINESS: { id: 'business', label: 'Business', icon: 'Building2', color: 'purple' },
  FINANCIAL: { id: 'financial', label: 'Financial', icon: 'DollarSign', color: 'green' },
  HEALTH: { id: 'health', label: 'Health & Fitness', icon: 'Heart', color: 'red' },
  RELATIONSHIPS: { id: 'relationships', label: 'Relationships', icon: 'Users', color: 'pink' },
  PERSONAL: { id: 'personal', label: 'Personal Growth', icon: 'Sparkles', color: 'amber' },
  SPIRITUAL: { id: 'spiritual', label: 'Spiritual', icon: 'Sun', color: 'yellow' },
  EDUCATION: { id: 'education', label: 'Education / Learning', icon: 'GraduationCap', color: 'indigo' },
  CREATIVE: { id: 'creative', label: 'Creative / Hobbies', icon: 'Palette', color: 'cyan' },
  COMMUNITY: { id: 'community', label: 'Community / Service', icon: 'Globe', color: 'teal' }
};

// Goal Priority
export const GOAL_PRIORITY = {
  CRITICAL: { id: 'critical', label: 'Critical', color: 'red', weight: 4 },
  HIGH: { id: 'high', label: 'High', color: 'orange', weight: 3 },
  MEDIUM: { id: 'medium', label: 'Medium', color: 'yellow', weight: 2 },
  LOW: { id: 'low', label: 'Low', color: 'gray', weight: 1 }
};

// Measure Types (Lead vs Lag)
export const MEASURE_TYPES = {
  LEAD: { 
    id: 'lead', 
    label: 'Lead Measure', 
    description: 'Actions you control that drive results',
    icon: 'Zap',
    examples: ['Calls made', 'Hours practiced', 'Chapters written', 'Workouts completed']
  },
  LAG: { 
    id: 'lag', 
    label: 'Lag Measure', 
    description: 'Results/outcomes from your actions',
    icon: 'Target',
    examples: ['Revenue generated', 'Weight lost', 'Book published', 'Deal closed']
  }
};

// Frequency Types
export const FREQUENCY_TYPES = {
  DAILY: { id: 'daily', label: 'Daily', multiplier: 7 },
  WEEKLY: { id: 'weekly', label: 'Weekly', multiplier: 1 },
  BIWEEKLY: { id: 'biweekly', label: 'Bi-weekly', multiplier: 0.5 },
  MONTHLY: { id: 'monthly', label: 'Monthly', multiplier: 0.25 }
};

// 12-Week Period Structure
export const TWELVE_WEEK_PERIOD = {
  TOTAL_WEEKS: 12,
  BUFFER_WEEK: 13, // Week 13 is for review and planning next period
  SCORING_THRESHOLD: 85, // % to be considered "on track"
  MINIMUM_SCORE: 60 // Below this, goal is at risk
};

// Weekly Scorecard Ratings
export const WEEKLY_RATINGS = {
  EXCELLENT: { id: 'excellent', label: 'Excellent', min: 90, color: 'green', emoji: '🔥' },
  GOOD: { id: 'good', label: 'Good', min: 75, color: 'blue', emoji: '👍' },
  AVERAGE: { id: 'average', label: 'Average', min: 60, color: 'yellow', emoji: '➡️' },
  POOR: { id: 'poor', label: 'Needs Work', min: 0, color: 'red', emoji: '⚠️' }
};

// Default Goal Structure
export const DEFAULT_GOAL = {
  // Identity
  id: '',
  title: '',
  description: '',
  vision: '', // Why this goal matters
  
  // Classification
  category: 'career',
  priority: 'medium',
  status: 'draft',
  
  // 12-Week Period
  periodId: '', // Reference to the 12-week period
  periodStartDate: null,
  periodEndDate: null,
  
  // Targets
  lagMeasure: {
    description: '', // What result you want
    targetValue: 0,
    currentValue: 0,
    unit: '', // e.g., "kg", "$", "clients"
    startingValue: 0
  },
  
  // Lead Measures (actions that drive results)
  leadMeasures: [], // Array of lead measure objects
  
  // Weekly Tactics
  weeklyTactics: [], // Specific actions for each week
  
  // Progress
  weeklyScores: [], // Array of weekly scorecard entries
  overallProgress: 0, // Calculated percentage
  
  // Workspace & Sharing
  workspaceId: null,
  visibility: 'private',
  sharedWith: [],
  accountabilityPartners: [], // Contact IDs
  
  // Linking
  linkedTasks: [], // Planner item IDs
  linkedMeetings: [], // Meeting IDs (weekly reviews)
  linkedNotes: [],
  
  // Metadata
  createdBy: '',
  createdAt: null,
  updatedAt: null,
  completedAt: null
};

// Lead Measure Structure
export const DEFAULT_LEAD_MEASURE = {
  id: '',
  description: '', // e.g., "Make prospecting calls"
  targetPerWeek: 0, // e.g., 20 calls per week
  unit: '', // e.g., "calls"
  frequency: 'weekly',
  weeklyActuals: {}, // { week1: 18, week2: 22, ... }
  isActive: true
};

// Weekly Scorecard Entry
export const DEFAULT_WEEKLY_SCORE = {
  weekNumber: 1,
  weekStartDate: '',
  weekEndDate: '',
  
  // Lead measure scores
  leadMeasureScores: [], // { measureId, target, actual, score }
  
  // Overall weekly score (0-100)
  overallScore: 0,
  
  // Reflection
  wins: '', // What went well
  challenges: '', // What was difficult
  learnings: '', // Key insights
  nextWeekFocus: '', // Priority for next week
  
  // Accountability
  reviewedAt: null,
  reviewedBy: '' // Could be self or accountability partner
};

// 12-Week Period Structure
export const DEFAULT_PERIOD = {
  id: '',
  name: '', // e.g., "Q1 2025 Sprint"
  startDate: '',
  endDate: '',
  
  // Vision
  periodVision: '', // What success looks like at end of 12 weeks
  
  // Goals for this period (typically 2-3 max)
  goalIds: [],
  
  // Model Week (ideal weekly schedule)
  modelWeek: {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: []
  },
  
  // Weekly Reviews
  weeklyReviewDay: 'friday', // Day for weekly review
  weeklyReviewTime: '16:00',
  
  // Status
  status: 'active', // draft, active, completed
  
  // Workspace
  workspaceId: null,
  
  // Metadata
  createdBy: '',
  createdAt: null,
  updatedAt: null
};

// Time Block Structure (for Model Week)
export const DEFAULT_TIME_BLOCK = {
  id: '',
  title: '', // e.g., "Strategic Block", "Buffer Block"
  startTime: '',
  endTime: '',
  type: 'strategic', // strategic, buffer, breakout
  linkedGoalId: null,
  linkedMeasureId: null,
  description: '',
  isRecurring: true
};

// Time Block Types
export const TIME_BLOCK_TYPES = {
  STRATEGIC: { 
    id: 'strategic', 
    label: 'Strategic Block', 
    description: 'High-value work on your goals',
    color: 'blue',
    minDuration: 180 // 3 hours recommended
  },
  BUFFER: { 
    id: 'buffer', 
    label: 'Buffer Block', 
    description: 'Admin, email, unexpected tasks',
    color: 'gray',
    minDuration: 30
  },
  BREAKOUT: { 
    id: 'breakout', 
    label: 'Breakout Block', 
    description: 'Meetings, calls, collaboration',
    color: 'green',
    minDuration: 60
  }
};

// Helper Functions

/**
 * Calculate current week number in a 12-week period
 */
export const getCurrentWeekNumber = (periodStartDate) => {
  const start = new Date(periodStartDate);
  const now = new Date();
  const diffTime = now - start;
  const diffWeeks = Math.floor(diffTime / (7 * 24 * 60 * 60 * 1000));
  return Math.min(Math.max(diffWeeks + 1, 1), 13); // Week 1-13
};

/**
 * Calculate goal progress percentage
 */
export const calculateGoalProgress = (goal) => {
  if (!goal.lagMeasure?.targetValue) return 0;
  
  const { startingValue = 0, currentValue = 0, targetValue } = goal.lagMeasure;
  const totalChange = targetValue - startingValue;
  const actualChange = currentValue - startingValue;
  
  if (totalChange === 0) return currentValue >= targetValue ? 100 : 0;
  return Math.min(Math.max(Math.round((actualChange / totalChange) * 100), 0), 100);
};

/**
 * Calculate weekly execution score
 */
export const calculateWeeklyScore = (leadMeasureScores) => {
  if (!leadMeasureScores?.length) return 0;
  
  const totalScore = leadMeasureScores.reduce((sum, score) => {
    const measureScore = score.target > 0 
      ? Math.min((score.actual / score.target) * 100, 100)
      : 0;
    return sum + measureScore;
  }, 0);
  
  return Math.round(totalScore / leadMeasureScores.length);
};

/**
 * Get rating based on score
 */
export const getScoreRating = (score) => {
  if (score >= WEEKLY_RATINGS.EXCELLENT.min) return WEEKLY_RATINGS.EXCELLENT;
  if (score >= WEEKLY_RATINGS.GOOD.min) return WEEKLY_RATINGS.GOOD;
  if (score >= WEEKLY_RATINGS.AVERAGE.min) return WEEKLY_RATINGS.AVERAGE;
  return WEEKLY_RATINGS.POOR;
};

/**
 * Calculate days remaining in period
 */
export const getDaysRemaining = (periodEndDate) => {
  const end = new Date(periodEndDate);
  const now = new Date();
  const diffTime = end - now;
  return Math.max(Math.ceil(diffTime / (24 * 60 * 60 * 1000)), 0);
};

/**
 * Generate week dates for a period
 */
export const generateWeekDates = (periodStartDate) => {
  const weeks = [];
  const start = new Date(periodStartDate);
  
  for (let i = 0; i < 12; i++) {
    const weekStart = new Date(start);
    weekStart.setDate(start.getDate() + (i * 7));
    
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    weeks.push({
      weekNumber: i + 1,
      startDate: weekStart.toISOString().split('T')[0],
      endDate: weekEnd.toISOString().split('T')[0]
    });
  }
  
  return weeks;
};

/**
 * Get next 12-week period start date (next Monday)
 */
export const getNextPeriodStartDate = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
  const nextMonday = new Date(now);
  nextMonday.setDate(now.getDate() + daysUntilMonday);
  return nextMonday.toISOString().split('T')[0];
};

/**
 * Calculate period end date (12 weeks from start)
 */
export const getPeriodEndDate = (startDate) => {
  const start = new Date(startDate);
  const end = new Date(start);
  end.setDate(start.getDate() + (12 * 7) - 1); // 12 weeks, ending on Sunday
  return end.toISOString().split('T')[0];
};